import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, delay, map, mergeMap } from 'rxjs/operators';

import { AppService } from '../app.service';
import { AuthService } from '../auth.service';

import { Talk } from '../shared/interfaces/talk';
import { User } from '../shared/interfaces/user';
import { Message } from '../shared/interfaces/message';
import { UserTiny } from '../shared/interfaces/user-tiny';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private _conversationToShow = new BehaviorSubject<number | null>(null);
  public readonly conversationToShow$ = this._conversationToShow.asObservable().pipe(delay(1));
  openConversation = new BehaviorSubject<boolean>(false)
  talkingId = new BehaviorSubject<number | null | undefined>(null)
  toggleSidenav = new BehaviorSubject<boolean>(false)
  componentDisplayed = new BehaviorSubject<string>('')
  relatedUserId = new BehaviorSubject<number | null>(null)
  relatedUser = new BehaviorSubject<UserTiny | null>(null)
  origin!: string
  usersContactedByDM: User["id"][] = []
  hideSilentTalkingNotification = new BehaviorSubject<number | null>(null)
  hideDeniedSilentTalking = new BehaviorSubject<number | null>(null)
  messageSent = new BehaviorSubject<{talkingId: number, message: string} | null>(null)
  roomToken!: string
  conversationClosed = new BehaviorSubject<string | null>(null)
  relationshipDeleted = new BehaviorSubject<boolean>(false)
  talk!: Talk

  constructor(
    private http: HttpClient,
    private appService: AppService,
    private authService: AuthService
  ) {}

  set conversationToShow(value: number | null) {
    this._conversationToShow.next(value);
  }

  setConversationToShow(value: number | null) {
    this._conversationToShow.next(value);
  }

  createConversation(relatedUserId: number): Observable<number> {
    const body = {
      related_user_id: relatedUserId.toString(),
    };
    return this.http.post<{ talking: { id: number } }>(`${this.appService.API}/api/talking`, body).pipe(
      map((res) => {
        return res.talking.id;
      }),
      catchError((error) => {
        return throwError(() => error)
      })
    );
  }

  getConversations(page: number, offset: number): Observable<Talk[]> {
    return this.http.get<{ talkings: Talk[] }>(`${this.appService.API}/api/talking/${page}/${offset}`).pipe(
      map((res) => {
        res.talkings.map((t) => {
          if (t.last_message) {
            t.last_message.created_at *= 1000;
          }
        });
        return res.talkings;
      }),
      catchError(error => {
        return throwError(() => error)
      })
    );
  }

  // silent talking is for sending direct messages between users with previously validated relationship
  askForSilentTalking(id: number): Observable<{data: {talking_id: number, room_talking: number}}> {
    const body = {
      related_user_id: id,
    }

    return this.http.post<{data: {talking_id: number, room_talking: number}}>(`${this.appService.API}/api/relationship/autovalidated`, body).pipe(
      map((res) => {
        return res
      }),
      catchError(error => {
        return throwError(() => error)
      })
    )
  }

  getMessagesCount(){
    return this.http.get<{new_messages: number}>(`${this.appService.API}/api/message/notifications`).pipe(
      map((res) => {
        return res.new_messages
      }),
      catchError((error) => {
        return throwError(() => error)
      })
    );
  }

  getMessages(talkId: number, lastMessageId: number | null): Observable<{ 
    messages: Message[]; 
    room: string; 
    user: User; 
    silent: boolean; 
    talking: { 
      user: { id: number }; 
      related_user: { id: number }
    }
  }> {
    const lastMessage = lastMessageId ? `/${lastMessageId}` : '';

    let userId: number
    this.authService.user.subscribe(res => {
      if(!res) return 
      userId = res?.id
    })

    return this.http
      .get<{
        messages: Message[];
        room_token: string;
        warning_silent_talking: boolean;
        talking: { user: { id: number }; related_user: { id: number } };
      }>(`${this.appService.API}/api/talking/messages/${talkId}/10${lastMessage}`)
      .pipe(
        mergeMap(({ messages, room_token, talking, warning_silent_talking }) => {
          return this.authService
            .getProfile(
              talking.user.id === userId ? talking.related_user.id : talking.user.id
            )
            .pipe(
              map((res) => {
                messages.map((m) => (m.created_at *= 1000));
                
                return {
                  messages,
                  room: room_token,
                  user: res,
                  talking: talking,
                  silent: warning_silent_talking,
                };
              })
            );
        })
      );
  }

  setAsRead(talk: number, message: number): Observable<{code: number}> {
    const body = { 
      last_message_id: message.toString(), 
      talking_id: talk.toString() 
    }

    return this.http.put<{code: number}>(`${this.appService.API}/api/message/read`, body).pipe(
      map((res) => {
        return res
      }),
      catchError((error) => {
        return throwError(() => error)
      })
    );
  }

  // change the state of a silent talk to convert it to a regular conversation
  changeSilentTalking(talkingId: number, state: string): Observable<void> {
    console.log('changeSilentTalking')
    const body = { 
      talking_id: talkingId.toString(), 
      silent: state 
    };

    return this.http.put(`${this.appService.API}/api/talking`, body).pipe(
      map(() => {
        return
      }),
      catchError((error) => {
        return throwError(() => error)
      })
    );
  }

  deleteTalking(id: number): Observable<{code: number}> {
    return this.http.delete<{code: number}>(`${this.appService.API}/api/talking?talking_id=${id}`).pipe(
      map((res) => {
        return res
      }),
      catchError((error) => {
        return throwError(() => error)
      })
    )
  }

  setSubscribe(subscriptionId: string) {
    const body = { subscription_id: subscriptionId };

    return this.http.post(`${this.appService.API}/billing/purchasing`, body).pipe(
      map((res) => {
        return res;
      }),
      catchError((error) => {
        return throwError(() => error)
      })
    );
  }
}
